<template>
  <!-- Add Dialog -->
  <TransitionRoot appear :show="!modalHidden" as="template">
    <Dialog
      as="div"
      @close="closeOnOutsideClick ? closeModal() : false"
      @keypress="keypress"
    >
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-50"
            leave="duration-200 ease-in"
            leave-from="opacity-50"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-50" />
          </TransitionChild>

          <span class="inline-block h-screen" aria-hidden="true"> &#8203; </span>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="inline-block w-full xl:max-w-4xl md:max-w-xl lg:max-w-2xl px-12 py-12 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                <span v-if="isEditMode">Edit {{ entityName }}</span>
                <span v-else>Add New {{ entityName }}</span>
              </DialogTitle>
              <Loading v-if="isSaveInProgress && !saveError" />
              <ErrorDisplay
                :label="`There's been an error saving this ${entityName}.<br />Please check your connection and try again.`"
                :message="saveError"
                :isVisible="saveError != ''"
              />
              <div class="mt-8">
                <div>
                  <slot></slot>
                </div>
                <div class="flex mt-8">
                  <button
                    type="button"
                    v-if="deleteEnabled && isEditMode"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-red-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    @click="deleteClick"
                  >
                    {{ deleteButtonText }}
                  </button>
                  <span class="flex-grow"></span>
                  <button
                    type="button"
                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    @click="saveBtnClick"
                  >
                    {{ saveButtonText }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex justify-center px-4 py-2 ml-3 text-sm font-medium text-blue-900 bg-gray-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    @click="closeModal"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect, watch } from "vue";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from "@headlessui/vue";

const isSaveInProgress = ref(false);

//Props with defaults
const props = withDefaults(
  defineProps<{
    entityName: string;
    saveError?: string;
    isVisible: boolean;
    isEditMode?: boolean;
    deleteEnabled?: boolean;
    saveOnEnter?: boolean;
    closeOnOutsideClick?: boolean;
    saveButtonText?: string;
    deleteButtonText?: string;
  }>(),
  {
    entityName: "entityName prop default",
    isVisible: false,
    isEditMode: false,
    deleteEnabled: false,
    saveOnEnter: true,
    closeOnOutsideClick: true,
    saveButtonText: "Save",
    deleteButtonText: "Delete",
  }
);

const emit = defineEmits<{
  (e: "save"): void;
  (e: "close"): void;
  (e: "delete"): void;
}>();

const modalHidden = computed(() => {
  // HACK:
  // This is a hack to ensure that the modal is hidden when the isVisible prop is false.
  // Mutating internal state in a computed property is not recommended,
  // but watch() and watchEffect() don't seem to be called in some cases.

  // console.log("isVisible: ", props.isVisible);
  if (!(props?.isVisible || false)) isSaveInProgress.value = false;

  return !(props?.isVisible ?? false);
});

watch(
  () => props?.isVisible || false,
  (newVal) => {
    //console.log(" >>EditPopup> Watch saw isVisible changed to: ", newVal);
    if (!newVal) {
      isSaveInProgress.value = false;
    }
  },
  { immediate: true, flush: "sync" }
);

watchEffect(() => {
  //console.log(" >> EditPopup> WatchEffect saw isVisible changed to: ", props.isVisible);
  if (!(props?.isVisible ?? false)) {
    isSaveInProgress.value = false;
  }
});

let closeModal = () => {
  isSaveInProgress.value = false;
  emit("close");
};

function saveBtnClick() {
  console.log("EditPopup.saveBtnClick");
  isSaveInProgress.value = true;
  emit("save");
}

function deleteClick() {
  isSaveInProgress.value = true;
  emit("delete");
}

function keypress(e: KeyboardEvent) {
  if (props.saveOnEnter && e.key === "Enter") {
    saveBtnClick();
  }
}
</script>
